import type { AnalysisResult, VarianceResult } from '../types'
import { ArrowUp, ArrowDown } from 'lucide-react'
import React from 'react'

/**
 * Standardized Arrow Component for Tables
 */
export const RenderArrow = ({ value }: { value: number | undefined | null }) => {
    if (value === undefined || value === null || value === 0) return null
    return value > 0 ? (
        <ArrowUp size={12} className="text-emerald-500 inline ml-1 align-middle" strokeWidth={3} />
    ) : (
        <ArrowDown size={12} className="text-rose-500 inline ml-1 align-middle" strokeWidth={3} />
    )
}

// ============================================================================
// INDIAN NUMBER FORMATTING (K / L / Cr)
// ============================================================================

/**
 * Format a number using Indian currency scale:
 * K  = Thousands (1,000)
 * L  = Lakhs (1,00,000)
 * Cr = Crores (1,00,00,000)
 *
 * GLOBAL RULE: If any history table or chart is created, use global history.
 */
export function formatIndianScale(value: number | undefined | null): string {
    if (value === undefined || value === null || isNaN(value)) return '-'
    const abs = Math.abs(value)
    const sign = value < 0 ? '-' : ''

    if (abs >= 1_00_00_000) {
        return `${sign}${(abs / 1_00_00_000).toFixed(2)} Cr`
    }
    if (abs >= 1_00_000) {
        return `${sign}${(abs / 1_00_000).toFixed(1)} L`
    }
    if (abs >= 1_000) {
        return `${sign}${(abs / 1_000).toFixed(1)} K`
    }
    return `${sign}${abs.toFixed(0)}`
}

/**
 * Format value based on column format type (used by Inspector tables)
 */
export function formatByColumnType(value: number | undefined | null, format: string): string {
    if (value === undefined || value === null || isNaN(value)) return '-'
    switch (format) {
        case 'lakh':
        case 'crore':
        case 'cumulative':
            return formatIndianScale(value)
        case 'percent':
            return `${value.toFixed(1)}%`
        case 'decimal':
            return value.toFixed(2)
        default:
            return String(value)
    }
}

// ============================================================================
// LABEL DEFINITIONS (Centralized)
// ============================================================================

export const STRIKE_LABELS = {
    RESISTANCE: 'R',
    RESISTANCE_2: 'R1',
    SUPPORT: 'S',
    SUPPORT_2: 'S1',
    SHIFT: 'Sh',
} as const

export const LABEL_CLASSES = {
    RESISTANCE:
        'absolute -top-2.5 -right-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-white border border-rose-200 text-[9px] font-black text-rose-600 shadow-sm z-10 ring-1 ring-rose-50',
    RESISTANCE_2:
        'absolute -top-2.5 -right-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-white border border-rose-200 text-[9px] font-black text-rose-600 shadow-sm z-10 ring-1 ring-rose-50',
    SUPPORT:
        'absolute -top-2.5 -right-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-white border border-emerald-200 text-[9px] font-black text-emerald-600 shadow-sm z-10 ring-1 ring-emerald-50',
    SUPPORT_2:
        'absolute -top-2.5 -right-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-white border border-emerald-200 text-[9px] font-black text-emerald-600 shadow-sm z-10 ring-1 ring-emerald-50',
    SHIFT: 'absolute -top-2.5 -right-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-white border border-indigo-200 text-[9px] font-black text-indigo-600 shadow-sm z-10 ring-1 ring-indigo-50',
} as const

// ============================================================================
// COLUMN DEFINITIONS (Centralized)
// ============================================================================

export const COLUMN_NAMES = {
    // Common Columns
    STRIKE: 'Strike',
    RANK: 'Rank',

    // Analysis Columns
    RESISTANCE: 'Resistance (R)',
    RESISTANCE_CE: 'Resistance (R) - CE',
    RESISTANCE_2: 'Resistance 2 (R1)',
    RESISTANCE_2_CE: 'Resistance 2 (R1) - CE',
    SUPPORT: 'Support (S)',
    SUPPORT_PE: 'Support (S) - PE',
    SUPPORT_2: 'Support 2 (S1)',
    SUPPORT_2_PE: 'Support 2 (S1) - PE',
    SHIFT: 'Shift',
    SHIFT_LEVEL: 'Shift', // Alias for Shift

    // Count Columns
    CE_COUNT: 'CE Count',
    PE_COUNT: 'PE Count',
    TOTAL_COUNT: 'Final Count',
    FINAL_COUNT: 'Final Count', // Alias for Total Count

    // Variance Columns
    OI_VARIANCE: 'OI % Variance',
    FINAL_OI_VARIANCE: 'OI % Variance', // Alias for OI % Variance
    MAX_OI_DIFF: 'Max OI',
    MIN_OI_DIFF: 'Min OI',
    MAX_VOL_DIFF: 'Max Final Volume % Diff',
    MIN_VOL_DIFF: 'Min Final Volume % Diff',
    MAX_STRIKE: 'MAX STRIKE',
    MIN_STRIKE: 'MIN STRIKE',

    // OI Columns
    MAX_OI: 'Max OI',
    MIN_OI: 'Min OI',

    // Other
    PCR: 'PCR',
} as const

// ============================================================================
// STRIKE LABEL HELPER
// ============================================================================

export interface StrikeLabel {
    text: string
    className: string
}

/**
 * Get the label and class for a given strike based on analysis results
 * This is the centralized function used across all tables
 */
export function getStrikeLabel(strike: number, analysis: AnalysisResult | null): StrikeLabel | null {
    if (!analysis) return null

    const matchedLabels: string[] = []
    let primaryClass = ''

    // Check Matches (Accumulate All)
    if (Math.abs(strike - analysis.shift) < 0.1) {
        matchedLabels.push(STRIKE_LABELS.SHIFT)
        // Shift is a pivot, usually neutral but we can style it validly
        if (!primaryClass) primaryClass = LABEL_CLASSES.SHIFT
    }

    if (strike === analysis.resistanceStrike) {
        matchedLabels.push(STRIKE_LABELS.RESISTANCE)
        if (!primaryClass) primaryClass = LABEL_CLASSES.RESISTANCE
    }

    if (analysis.resistanceStrike2 && strike === analysis.resistanceStrike2) {
        matchedLabels.push(STRIKE_LABELS.RESISTANCE_2)
        if (!primaryClass) primaryClass = LABEL_CLASSES.RESISTANCE_2
    }

    if (strike === analysis.supportStrike) {
        matchedLabels.push(STRIKE_LABELS.SUPPORT)
        if (!primaryClass) primaryClass = LABEL_CLASSES.SUPPORT
    }

    if (analysis.supportStrike2 && strike === analysis.supportStrike2) {
        matchedLabels.push(STRIKE_LABELS.SUPPORT_2)
        if (!primaryClass) primaryClass = LABEL_CLASSES.SUPPORT_2
    }

    // Check tableRows only if no standard labels found (Logic preservation)
    // Or should we append? Usually standard labels override row labels.
    if (matchedLabels.length === 0 && analysis.tableRows) {
        const row = analysis.tableRows.find((r) => r.strike === strike)
        if (row?.label) {
            return {
                text: row.label,
                className: row.labelClass || '',
            }
        }
    }

    if (matchedLabels.length > 0) {
        return {
            text: matchedLabels.join(','),
            className: primaryClass,
        }
    }

    return null
}

// ============================================================================
// TABLE STYLING CLASSES (Centralized Design System)
// ============================================================================

/**
 * UNIFIED TABLE DESIGN SYSTEM
 * Use these classes consistently across ALL tables for visual harmony
 */
export const TABLE_CLASSES = {
    // Container - White card with subtle shadow
    CONTAINER:
        'overflow-x-auto custom-scrollbar bg-white dark:bg-slate-900 rounded-[20px] shadow-xl border border-slate-100 dark:border-slate-800 max-w-full',

    // Table
    TABLE: 'w-full border-separate border-spacing-0',

    // Header Row - Light pastel backgrounds
    THEAD: 'sticky top-0 z-30',

    // Header Cells - Unified light design
    TH: 'py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 border-b-2 border-slate-200 dark:border-slate-700 whitespace-nowrap',
    TH_CALL:
        'py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-500 bg-rose-50 dark:bg-rose-900/20 border-b-2 border-rose-200 dark:border-rose-800 whitespace-nowrap',
    TH_PUT: 'py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 border-b-2 border-emerald-200 dark:border-emerald-800 whitespace-nowrap',
    TH_STRIKE:
        'py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 border-b-2 border-slate-200 dark:border-slate-700 min-w-[100px] text-center whitespace-nowrap',
    TH_SHIFT:
        'py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 border-b-2 border-indigo-200 dark:border-indigo-800 min-w-[80px] whitespace-nowrap',

    // Body
    TR: 'group transition-colors duration-150 hover:bg-slate-50/50 dark:hover:bg-slate-800/50',
    TR_ALT: 'bg-slate-25',

    // Data Cells - Clean and minimal
    TD: 'py-3 px-4 text-sm font-medium text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-800 whitespace-nowrap',
    TD_MONO:
        'py-3 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-800 whitespace-nowrap font-mono tabular-nums',
    TD_CALL:
        'py-3 px-4 text-sm font-semibold text-rose-700 dark:text-rose-400 border-b border-slate-100 dark:border-slate-800 bg-rose-50/30 dark:bg-rose-900/10 whitespace-nowrap',
    TD_PUT: 'py-3 px-4 text-sm font-semibold text-emerald-700 dark:text-emerald-400 border-b border-slate-100 dark:border-slate-800 bg-emerald-50/30 dark:bg-emerald-900/10 whitespace-nowrap',
    TD_STRIKE:
        'py-3 px-4 text-center border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 whitespace-nowrap',
    TD_SHIFT: 'py-3 px-4 text-center border-b border-slate-100 dark:border-slate-800 whitespace-nowrap',

    // Sticky columns
    TD_STICKY:
        'sticky left-0 z-10 bg-white dark:bg-slate-900 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] whitespace-nowrap',

    // Strike Chips (KEEP UNCHANGED as requested)
    STRIKE_CHIP:
        'premium-strike-chip relative mx-auto w-24 py-1.5 rounded-lg border inline-flex items-center justify-center font-bold text-sm premium-cell-mono shadow-sm transition-all duration-200 dark:bg-slate-800 dark:border-slate-700',
    VARIANCE_CHIP:
        'premium-strike-chip relative mx-auto w-28 py-1.5 rounded-lg border inline-flex items-center justify-center font-bold text-sm premium-cell-mono shadow-sm transition-all duration-200 dark:bg-slate-800 dark:border-slate-700',
    SHIFT_CHIP:
        'premium-strike-chip text-xs font-black px-3 py-1.5 rounded-lg shadow-sm ring-1 ring-white/50 dark:ring-white/10 backdrop-blur-md inline-flex items-center justify-center',
    STRIKE_CHIP_CE: 'premium-strike-chip-ce',
    STRIKE_CHIP_PE: 'premium-strike-chip-pe',
    STRIKE_CHIP_RESISTANCE:
        'bg-rose-50 dark:bg-rose-900/30 border-rose-200 dark:border-rose-800 text-rose-800 dark:text-rose-300 ring-1 ring-rose-100 dark:ring-rose-900/50',
    STRIKE_CHIP_SUPPORT:
        'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 ring-1 ring-emerald-100 dark:ring-emerald-900/50',
    STRIKE_CHIP_NEUTRAL:
        'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-slate-800 dark:text-slate-300 hover:border-blue-400 hover:text-blue-700 hover:shadow-md hover:scale-105',
    STRIKE_CHIP_ATM:
        'bg-amber-100 dark:bg-amber-900/40 border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200 ring-1 ring-amber-200 dark:ring-amber-800/50',
} as const

/**
 * TABLE HEADER DESIGN
 * Unified header widget design for all tables
 */
export const TABLE_HEADER = {
    // Container for table header (icon + title + controls)
    CONTAINER:
        'px-6 py-5 flex flex-wrap gap-y-4 justify-between items-center border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-t-[20px]',

    // Icon wrapper
    ICON_WRAPPER: 'p-2.5 rounded-xl shadow-sm ring-1',
    ICON_GREEN: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
    ICON_BLUE: 'bg-indigo-50 text-indigo-600 ring-indigo-100',
    ICON_AMBER: 'bg-amber-50 text-amber-600 ring-amber-100',
    ICON_PURPLE: 'bg-purple-50 text-purple-600 ring-purple-100',

    // Title
    TITLE: 'text-lg font-black text-slate-800 dark:text-slate-200 tracking-tight',
    SUBTITLE: 'text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-medium',
} as const

/**
 * CALLS/PUTS GROUP HEADER
 * For option chain style tables with two sides
 */
export const GROUP_HEADER = {
    WRAPPER: 'flex w-full shadow-sm',
    CALLS_SIDE:
        'flex-1 bg-linear-to-b from-white to-rose-50 dark:from-slate-900 dark:to-rose-900/20 border-b-2 border-rose-200 dark:border-rose-800 p-2 text-center',
    PUTS_SIDE:
        'flex-1 bg-linear-to-b from-white to-emerald-50 dark:from-slate-900 dark:to-emerald-900/20 border-b-2 border-emerald-200 dark:border-emerald-800 p-2 text-center',
    STRIKE_SPACER:
        'w-[100px] md:w-[124px] bg-slate-100 dark:bg-slate-800 border-b-2 border-slate-200 dark:border-slate-700',
    BADGE_CALLS:
        'inline-flex px-4 py-1.5 rounded-full text-[10px] font-black tracking-widest border border-rose-200 dark:border-rose-800 bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 shadow-sm',
    BADGE_PUTS:
        'inline-flex px-4 py-1.5 rounded-full text-[10px] font-black tracking-widest border border-emerald-200 dark:border-emerald-800 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 shadow-sm',
} as const

export const HEADER_CLASSES = {
    // Standard Headers
    CALLS: 'py-3 px-2 text-[11px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-500 bg-rose-50 dark:bg-rose-900/20 border-b-2 border-rose-200 dark:border-rose-800',
    PUTS: 'py-3 px-2 text-[11px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 border-b-2 border-emerald-200 dark:border-emerald-800',
    NEUTRAL:
        'py-3 px-2 text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 border-b-2 border-slate-200 dark:border-slate-700',
    GROUP_CALLS:
        'text-center text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 border-b-2 border-rose-200 dark:border-rose-800 text-xs font-black uppercase tracking-[0.15em] py-3',
    GROUP_PUTS:
        'text-center text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-b-2 border-emerald-200 dark:border-emerald-800 text-xs font-black uppercase tracking-[0.15em] py-3',

    // Sticky
    STRIKE_STICKY:
        'sticky top-[46px] z-30 bg-slate-100 dark:bg-slate-800 border-b-2 border-slate-200 dark:border-slate-700 text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 min-w-[100px] text-center py-3 px-2',

    // Extra
    INFO: 'py-3 px-2 text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 border-b-2 border-slate-200 dark:border-slate-700',
    HIGHLIGHT:
        'py-3 px-2 text-[11px] font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 border-b-2 border-indigo-200 dark:border-indigo-800',
} as const

// ============================================================================
// CHART COLORS (Centralized)
// ============================================================================

export const CHART_COLORS = {
    POSITIVE: '#10B981', // emerald-500
    NEGATIVE: '#F43F5E', // rose-500
    NEUTRAL: '#6366F1', // indigo-500
} as const

// ============================================================================
// COLOR CODING HELPERS
// ============================================================================

/**
 * Get color class for count values (CE/PE/Total Count)
 */
export function getCountColorClass(value: number | undefined | null): string {
    if (value === undefined || value === null) return 'text-slate-500 dark:text-slate-400'
    if (value > 0) return 'text-emerald-600! dark:text-emerald-400!'
    if (value < 0) return 'text-rose-600! dark:text-rose-400!'
    return 'text-slate-500 dark:text-slate-400'
}

/**
 * Get formatted count with + prefix for positive values
 */
export function formatCount(value: number | undefined | null): string {
    if (value === undefined || value === null) return '0'
    if (value > 0) return `+${value}`
    return value.toString()
}

/**
 * Get color class for variance values
 */
export function getVarianceColorClass(value: number | undefined | null): string {
    if (value === undefined || value === null)
        return 'bg-slate-50 dark:bg-slate-800 text-slate-700! dark:text-slate-300! border-slate-200 dark:border-slate-700'
    if (value > 0)
        return 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700! dark:text-emerald-400! border-emerald-200 dark:border-emerald-800'
    if (value < 0)
        return 'bg-rose-50 dark:bg-rose-900/30 text-rose-700! dark:text-rose-400! border-rose-200 dark:border-rose-800'
    return 'bg-slate-50 dark:bg-slate-800 text-slate-700! dark:text-slate-300! border-slate-200 dark:border-slate-700'
}

/**
 * Get color class for Shift values
 */
export function getShiftColorClass(value: number | undefined | null): string {
    if (value === undefined || value === null)
        return 'bg-slate-50 dark:bg-slate-800 text-slate-700! dark:text-slate-300! border-slate-200 dark:border-slate-700'
    if (value > 0)
        return 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700! dark:text-emerald-400! border-emerald-200 dark:border-emerald-800'
    if (value < 0)
        return 'bg-rose-50 dark:bg-rose-900/30 text-rose-700! dark:text-rose-400! border-rose-200 dark:border-rose-800'
    return 'bg-slate-50 dark:bg-slate-800 text-slate-700! dark:text-slate-300! border-slate-200 dark:border-slate-700'
}

/**
 * Get text color class for positive/negative values
 */
export function getValueColorClass(value: number | undefined | null): string {
    if (value === undefined || value === null) return 'text-slate-500'
    if (value > 0) return 'text-emerald-600!'
    if (value < 0) return 'text-rose-600!'
    return 'text-slate-500'
}

/**
 * Get color class for strike indicators
 */
export function getStrikeChipStyle(isAtm: boolean, label: string | null): string {
    if (isAtm) {
        return TABLE_CLASSES.STRIKE_CHIP_ATM
    }

    if (label === STRIKE_LABELS.RESISTANCE || label === STRIKE_LABELS.RESISTANCE_2) {
        return TABLE_CLASSES.STRIKE_CHIP_RESISTANCE
    }

    if (label === STRIKE_LABELS.SUPPORT || label === STRIKE_LABELS.SUPPORT_2) {
        return TABLE_CLASSES.STRIKE_CHIP_SUPPORT
    }

    if (label === STRIKE_LABELS.SHIFT) {
        return TABLE_CLASSES.STRIKE_CHIP_NEUTRAL + ' border-indigo-200 text-indigo-700 dark:text-indigo-400'
    }

    return TABLE_CLASSES.STRIKE_CHIP_NEUTRAL
}

export function getStrikeColorClass(label: string | null): string {
    return getStrikeChipStyle(false, label)
}

// ============================================================================
// CENTRALIZED CALCULATION GETTERS
// ============================================================================

/**
 * Extract all key metrics from analysis result
 * This ensures consistent access across all components
 */
export interface CentralizedMetrics {
    // Strikes
    resistanceStrike: number
    resistanceStrike2?: number
    supportStrike: number
    supportStrike2: number
    shift: number
    atmStrike: number

    // Counts
    ceCount: number
    peCount: number
    totalCount: number

    // Variance
    maxOIDiff: number
    minOIDiff: number
    maxStrike: number
    minStrike: number
    finalVariance: number

    // OI
    maxOI: number
    minOI: number

    // Other
    pcr: number
    rank?: number
}

export function getCentralizedMetrics(
    analysis: (AnalysisResult & { variance: VarianceResult | null; atmStrike: number }) | null,
): CentralizedMetrics | null {
    if (!analysis) return null

    const variance = analysis.variance

    return {
        // Strikes
        resistanceStrike: analysis.resistanceStrike,
        supportStrike: analysis.supportStrike,
        supportStrike2: analysis.supportStrike2,
        shift: analysis.shift,
        atmStrike: analysis.atmStrike,

        // Counts (moved to root AnalysisResult, not in VarianceResult)
        ceCount: (analysis as any).ceCount || 0,
        peCount: (analysis as any).peCount || 0,
        totalCount: (analysis as any).totalCount || 0,

        // Variance
        maxOIDiff: variance?.maxDiff || 0,
        minOIDiff: variance?.minDiff || 0,
        maxStrike: variance?.maxStrike || 0,
        minStrike: variance?.minStrike || 0,
        finalVariance: variance?.finalVariance || 0,

        // OI
        maxOI: analysis.maxCallOI || 0,
        minOI: 0,

        // Other
        pcr: analysis.pcr || 0,
    }
}

// ============================================================================
// RESPONSIVE TABLE WRAPPER
// ============================================================================

/**
 * Standard table wrapper component props
 */
export interface TableWrapperProps {
    children: React.ReactNode
    className?: string
}

/**
 * Get standardized table wrapper classes
 */
export function getTableWrapperClasses(additionalClasses?: string): string {
    const baseClasses = TABLE_CLASSES.CONTAINER
    return additionalClasses ? `${baseClasses} ${additionalClasses}` : baseClasses
}

// ============================================================================
// COLUMN WIDTH HELPERS
// ============================================================================

export const COLUMN_WIDTHS = {
    STRIKE: 'min-w-[140px] md:min-w-[180px]',
    SHIFT: 'min-w-[100px] md:min-w-[110px]',
    RANK: 'min-w-[60px] md:min-w-[70px]',
    COUNT: 'min-w-[90px] md:min-w-[100px]',
    VARIANCE: 'min-w-[110px] md:min-w-[120px]',
    DEFAULT: 'min-w-[100px] md:min-w-[120px]',
} as const

/**
 * Get column width class based on column type
 */
export function getColumnWidth(columnName: string): string {
    if (columnName.includes('Strike') || columnName.includes('STRIKE')) {
        return COLUMN_WIDTHS.STRIKE
    }
    if (columnName.includes('Shift') || columnName.includes('SHIFT')) {
        return COLUMN_WIDTHS.SHIFT
    }
    if (columnName.includes('Rank') || columnName.includes('RANK')) {
        return COLUMN_WIDTHS.RANK
    }
    if (columnName.includes('Count') || columnName.includes('COUNT')) {
        return COLUMN_WIDTHS.COUNT
    }
    if (columnName.includes('Variance') || columnName.includes('Diff')) {
        return COLUMN_WIDTHS.VARIANCE
    }
    return COLUMN_WIDTHS.DEFAULT
}
